/* Copyright (c) 2017 Backpack Health LLC */

import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';

import HealthEntryNameText from './HealthEntryNameText';
import ShareCardIncludedTypeNames from '../../constants/ShareCardIncludedTypeNames';
import GeneAttributesListResultText from './GeneAttributesListResultText';
import { isGeneticResultCategory, getHealthEntryCategoryData } from '../../constants/HealthEntryCategoryNames';
import HealthEntrySummaryTemplate, { ShortSummaryTemplate } from './HealthEntrySummaryTemplate';
import HealthEntrySubTitleText from './HealthEntrySubTitleText';


const hasAttachments = (healthEntry) => {
  if (healthEntry.values && healthEntry.values.hasValues()) {
    return healthEntry.values.hasAnyAttachments();
  } else {
    return healthEntry.attachments.hasAttachments();
  }
};

const hasComments = (healthEntry) => {
  if (healthEntry.values && healthEntry.values.hasValues()) {
    return healthEntry.values.hasAnyComments();
  } else {
    return healthEntry.comments.hasComments();
  }
};

const isFlagged = (healthEntry) => {
  if (healthEntry.values && healthEntry.values.hasValues()) {
    return healthEntry.values.hasAnyFlagged();
  } else {
    return healthEntry.flag.isFlagged();
  }
};

interface Props {
  healthEntry: any;
  languageCode: string;
  color?: string;
  areIconsAttachedToRight?: boolean;
  showCategory?: boolean;
}
class HealthEntrySummary extends Component<Props> {

  renderGeneAdditionalAttributes() {
    const { healthEntry, languageCode } = this.props;
    const { category, values } = healthEntry;

    if (!values) return null;
    if (!isGeneticResultCategory(category)) return null;

    const mostRecentValue = values.mostRecent();

    if (!mostRecentValue) return null;

    const { geneVariantResult } = mostRecentValue;

    return (
      <GeneAttributesListResultText
        className="font-footnote cmp-hesummary-gene-attributes"
        geneVariantResult={geneVariantResult}
        languageCode={languageCode}
      />
    );
  }

  render() {
    const { color, healthEntry, areIconsAttachedToRight, showCategory } = this.props;

    const categoryData  = showCategory ? getHealthEntryCategoryData(healthEntry.data()) : null;
    const showHeValues = healthEntry.values && healthEntry.values.count() !== 1;
    const olderHEValuesCount = (showHeValues && healthEntry.values.count() > 0) ? healthEntry.values.count() - 1 : 0;
    const olderCountPhrase = olderHEValuesCount > 0 ? 'healthEntry.value.count.text' : 'healthEntry.occurrenceCount.message.text';

    return (
      <HealthEntrySummaryTemplate
        color={color}
        title={
          <HealthEntryNameText healthEntry={healthEntry} includedValues={ShareCardIncludedTypeNames.latest} className="util-text-truncate" />
        }
        additionalTitle={this.renderGeneAdditionalAttributes()}
        subTitle={(
          <>
            <HealthEntrySubTitleText
              healthEntry={healthEntry}
              showAttachments={!areIconsAttachedToRight}
            />
            <div>
              {
                showHeValues
                ? <FormattedMessage id={olderCountPhrase} values={{ digit1: olderHEValuesCount }} />
                : null
              }
            </div>
          </>
        )}
        subTitle2={(
          categoryData
          ? <FormattedMessage {...categoryData.titlePhrase} />
          : null
        )}
        areIconsAttachedToRight={areIconsAttachedToRight}
        hasAttachments={hasAttachments(healthEntry)}
        hasComments={hasComments(healthEntry)}
        isFlagged={isFlagged(healthEntry)}
      />
    );
  }
}

const GeneticAttributeSummary = (props) => {
  const { healthEntry, languageCode } = props;
  const { category, values } = healthEntry;

  if (!values) return null;
  if (!isGeneticResultCategory(category)) return null;

  const mostRecentValue = values.mostRecent();

  if (!mostRecentValue) return null;

  const { geneVariantResult } = mostRecentValue;

  return (
    <GeneAttributesListResultText
      className="font-footnote cmp-hesummary-gene-attributes"
      geneVariantResult={geneVariantResult}
      languageCode={languageCode}
    />
  );
};

const HealthEntryValueSummary = (props) => {
  const { healthEntry } = props;
  const showHeValues = healthEntry.values && healthEntry.values.count() !== 1;

  if (!showHeValues) { return null; }

  const olderHEValuesCount = (showHeValues && healthEntry.values.count() > 0) ? healthEntry.values.count() - 1 : 0;
  const olderCountPhrase = olderHEValuesCount > 0 ? 'healthEntry.value.count.text' : 'healthEntry.occurrenceCount.message.text';

  return (
    <FormattedMessage id={olderCountPhrase} values={{ digit1: olderHEValuesCount }} />
  );
};

export const HealthEntryShortSummary = (props) => {
  const { color, healthEntry, areIconsAttachedToRight, showCategory } = props;
  const categoryData  = showCategory ? getHealthEntryCategoryData(healthEntry.data()) : null;

  return (
    <ShortSummaryTemplate areIconsAttachedToRight color={color}>
      <HealthEntryNameText healthEntry={healthEntry} includedValues={ShareCardIncludedTypeNames.latest} style={{ wordBreak: 'break-word' }} />

      <GeneticAttributeSummary {...props} />

      <HealthEntrySubTitleText
        healthEntry={healthEntry}
        showAttachments={!areIconsAttachedToRight}
      />

      <HealthEntryValueSummary healthEntry={healthEntry} />

      {
        categoryData
        ? <FormattedMessage {...categoryData.titlePhrase} />
        : null
      }
    </ShortSummaryTemplate>
  );
};

export default HealthEntrySummary;
